import { useQuery } from 'src/shared/lib/useQuery';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { genQueryKey } from 'src/shared/lib/query.utils';
import { QueryResult } from 'src/shared/types/util.types';
import { MessageType, MessageActions } from 'src/features/messages/types/message.types';
import { useModels } from 'src/features/models/queries/useModels';
import { isSimpleFacet } from 'src/shared/lib/utils';
import { IntegrationId } from 'src/shared/types/integration.types';

type ThreadMetadataResponse = {
  integration_id: IntegrationId;
  thread_id: string;
  facets?: Record<string, string[] | any[]>;
  entities?: Record<string, any>;
  attachments?: any[];
  actions?: any[];
  actuals?: Record<string, any>;
  facets_versions?: Record<string, string>;
};

export function useThreadMetadata(integrationId?: string, threadId?: string): QueryResult<MessageType | null> {
  const { orgFetch, currentOrg } = useOrg();
  const { data: allModels } = useModels();

  const queryKey = ['thread-metadata', genQueryKey(currentOrg.id, integrationId!, threadId!)];

  const query = useQuery({
    queryKey: queryKey,
    queryFn: getData,
    enabled: !!integrationId && !!threadId && !!allModels,
    staleTime: 30 * 1000, // Cache for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  async function getData(): Promise<MessageType | null> {
    if (!integrationId || !threadId || !allModels) {
      return null;
    }

    const res = await orgFetch<ThreadMetadataResponse>(
      `/api/v2/plugin/message?integrationId=${integrationId}&threadId=${threadId}`,
      {
        method: 'get',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (res.status === 404) {
      return null;
    }

    if (!res.ok) {
      throw new Error('Failed to fetch thread metadata');
    }

    const data = await res.json();

    // Transform the response to match MessageType structure
    return transformToMessageType(data, allModels);
  }

  return query;
}

function transformToMessageType(data: ThreadMetadataResponse, allModels: any[]): MessageType {
  let models: MessageType['models'] = {};
  let entities: MessageType['entities'] = {};

  // Process facets - filter out removed models and only keep simple facets in models
  if (data.facets) {
    Object.entries(data.facets)
      .filter(([facetId]) => allModels.some((model) => model.id === facetId))
      .forEach(([facet, value]) => {
        if (isSimpleFacet(value)) {
          models[facet] = value;
        }
      });
  }

  // Process entities - filter out removed models
  if (data.entities) {
    Object.entries(data.entities)
      .filter(([modelId]) => {
        // Allow extraction models (next-gen) through
        if (modelId.startsWith('ext_mdl_')) {
          return true;
        }
        // For legacy models, check if they exist in allModels
        return allModels.some((model) => model.id === modelId);
      })
      .forEach(([modelId, values]) => {
        entities[modelId] = values;
      });
  }

  // Group actions by flow_id
  const groupedActions =
    data.actions?.reduce(
      (acc, action) => {
        const flowId = action.flow_id || 'no_flow';
        if (!acc[flowId]) {
          acc[flowId] = [];
        }
        acc[flowId].push(action as MessageActions);
        return acc;
      },
      {} as Record<string, MessageActions[]>
    ) || {};

  // Filter attachments (remove image placeholders)
  const attachments =
    data.attachments?.filter((attachment) => {
      if (!attachment.name) return false;
      return !attachment.name.match(/image\d+/) && !attachment.name.match(/^\d+\.(jpg|jpeg|png|gif|webp|svg)$/i);
    }) || [];

  return {
    thread_id: data.thread_id,
    integration_id: data.integration_id,
    time: Date.now() / 1000, // Plugin endpoint doesn't provide timestamp, use current time
    message_count: 1, // Plugin endpoint doesn't provide message count, use default
    facets: data.facets || {},
    models,
    entities,
    facets_versions: data.facets_versions || {},
    attachments,
    actions: (data.actions as MessageActions[]) || [],
    groupedActions,
    actuals: data.actuals || {},
    // These fields are not provided by the plugin endpoint, so we use defaults
    message: {} as any, // Not needed for the Outlook plugin UI
  };
}
